import React from 'react';
import { ArcStack, ArcCellInfo, ArcButton, ArcText, ArcSwitch } from '@src/core/ui';
import { ZoneDetailsNoFlyPopupProps } from './types';
import { useDispatch } from 'react-redux';
import { setSelectedZone, useDeleteZoneMutation, useUpdateZoneMutation } from '@src/features/zones/redux';
import ZonePopupWrapper from '@src/features/zones/components/zone-popup-wrapper';
import { useNavigate } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { toast } from 'react-toastify';
import { arcToFixed } from '@src/core/extensions';
import { ZoneEntity, ZoneShapeType, ZoneType } from '@src/core/features';
import { ZoneTypeName } from '@src/features/zones/config/zones-constants';

const ZoneDetailsPopupNoFly: React.FC<ZoneDetailsNoFlyPopupProps> = (props) => {
  const { zone = {} as ZoneEntity } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleteZone ] = useDeleteZoneMutation();
  const [updateZone ] = useUpdateZoneMutation();

  const getZoneInfo = () => {
    return {
      id: zone?.id || "N/A",
      name: zone?.name || "N/A",
      description: zone?.description || "N/A",
      locationName: zone?.locationName || "N/A",
      areaSquareMeters: zone?.areaSquareMeters || 0,
      radiusMeters: zone?.radiusMeters || 0,
      shape: zone?.shape || "N/A",
      zoneType: zone?.zoneType || "N/A",
      isActive: zone?.isActive || false,
    };
  };

  const zoneInfo = getZoneInfo();

  const onToggleActive = async () => {
    if (!zone?.id) return;

    try {
      await updateZone({
        id: zone.id,
        name: zone.name,
        description: zone.description,
        locationName: zone.locationName,
        centerLatitude: zone.centerLatitude,
        centerLongitude: zone.centerLongitude,
        radiusMeters: zone.radiusMeters,
        altitudeMeters: zone.altitudeMeters,
        shape: zone.shape,
        zoneType: zone.zoneType,
        isActive: !zone.isActive,
        polygonCoordinates: zone.polygonCoordinates,
      }).unwrap();
      toast.success(`Zone ${zone?.isActive ? 'deactivated' : 'activated'} successfully`);
      onClose();
    } catch (error) {
      toast.error(`Failed to ${zone?.isActive ? 'deactivate' : 'activate'} zone`);
    }
  };

  const onClose = () => {
    dispatch(setSelectedZone(null));
  };

  const onEditClick = () => {

    const stage = zone?.shape === ZoneShapeType.CIRCLE ? 'stage=1' : 'stage=2';
    
    navigate(`${routesConfig.editZone}/${zone?.id}?${stage}`);
    onClose();
  };

  const onDeleteClick = async () => {
    try {
      await deleteZone({ id: zone?.id ?? 0 }).unwrap();
      toast.success('Zone deleted successfully');
      onClose();
      navigate(routesConfig.zones);
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  if (!zone?.id) {
    return null;
  }

  return (
    <ZonePopupWrapper title="ZONE DETAILS">
      <ArcStack spacing={2}>
        <ArcStack>
          <ArcStack direction="row" spacing={4} marginTop={1}>
            <ArcCellInfo label="Type" textProps={{ fontWeight: 400 }} flex={1}>
              {ZoneTypeName[zoneInfo.zoneType as ZoneType]}
            </ArcCellInfo>

            <ArcCellInfo label="Location" textProps={{ fontWeight: 400 }} flex={1}>
              {zoneInfo.locationName}
            </ArcCellInfo>
          </ArcStack>
        </ArcStack>
        <ArcStack direction="row" spacing={4}>
          <ArcCellInfo label="Name" textProps={{ sx: { wordBreak: 'break-word' } }} flex={1}>
            {zoneInfo.name}
          </ArcCellInfo>

          <ArcCellInfo label="Area" textProps={{ fontWeight: 400 }} flex={1}>
            {arcToFixed(zoneInfo.areaSquareMeters)} m²
          </ArcCellInfo>
        </ArcStack>
        <ArcCellInfo
          label="Description"
          textProps={{ sx: { wordBreak: 'break-word' }, fontWeight: 400 }}
        >
          {zoneInfo.description}
        </ArcCellInfo>
        <ArcStack>
          <ArcStack direction="row" spacing={1} justifyContent="space-between" mt={1}>
            <ArcButton.ghost fullWidth onClick={onEditClick} size="large">
          Edit
            </ArcButton.ghost>
            <ArcButton.ghost fullWidth onClick={onDeleteClick} size="large">
          Delete
            </ArcButton.ghost>
          </ArcStack>
        </ArcStack>
        <ArcStack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <ArcText.button textTransform="none" fontSize={15} fontWeight={400}>
          Activate/Deactivate zone
          </ArcText.button>
          <ArcSwitch.brand
            checked={zone?.isActive}
            onChange={onToggleActive}
            sx={{
              '& .MuiSwitch-track': { backgroundColor: 'background.default' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'background.default',
              },
            }}
          />
        </ArcStack>
      </ArcStack>
    </ZonePopupWrapper>
  );



};

export default ZoneDetailsPopupNoFly;